import { ApplicationError } from "../../models/errors/application-error";
import IProductService from "./product-service-interface";
import { ApplicationProductList } from "../../models/product/application-product-list";
import { ApplicationProductDetail } from "../../models/product/application-product-detail";
import { mapStringToEnum } from "../../helpers/enum-helper";
import { ApplicationBestSupplierProduct } from "../../models/product/application-best-supplier-product";
import { ApplicationDay, ApplicationDeliveryDay } from "../../models/supplier/application-day";
import client from "../../external/open-api";
import { components } from "../../external/schema/order-link-api-schema";
import { ApplicationProduct } from "../../models/product/application-product";
import { ApplicationSizeUnit } from "../../models/product/application-size-unit";
import BaseService from "../base-service";
import { ApplicationVAT } from "../../models/finance/application-vat";
import { ApplicationOrderTemplate } from "../../models/organisation/application-order-template";
import { ApplicationOrderingMode } from "../../models/organisation/application-ordering-mode";
import { ApplicationProductSelectionMode } from "../../models/organisation/application-product-selection-mode";
import { ApplicationSubDepartment } from "../../models/organisation/application-sub-department";
import { ApplicationDepartment } from "../../models/organisation/application-department";
import { ApplicationProductCostPriceHistory } from "../../models/product/application-product-cost-price-history";

export default class ProductService extends BaseService implements IProductService {
  async searchSupplierProducts(
    supplierId: number,
    searchText: string,
    currentPosition: number,
    fetchNext: number,
    fetchAll: boolean
  ): Promise<ApplicationProductList> {
    const { data, error } = await this.withRetry(() => client.GET(
      "/v{version}/api/product/supplier/{supplierId}",
      {
        params: {
          path: { version: "1", supplierId }, // Path parameter
          query: {
            "Paging.CurrentPosition": currentPosition,
            "Paging.FetchNext": fetchNext,
            "Paging.FetchAll": fetchAll,
            searchText: searchText,
          },
        },
      }
    ));

    if (!data?.data || !data?.success || error) {
      throw new ApplicationError("Failed to search products");
    }

    const products = this.mapToApplicationProduct(data.data);

    return {
      products,
      loading: false,
      count: data.data?.count || 0,
      currentPosition: data.data?.currentPosition || 0,
    };
  }

  async searchProducts(
    branchId: number,
    subDepartmentId: number,
    searchText: string,
    currentPosition: number,
    fetchNext: number,
    fetchAll: boolean
  ): Promise<ApplicationProductList> {
    const { data, error } = await this.withRetry(() => client.GET(
      "/v{version}/api/product/branch/{branchId}/subdepartment/{subDepartmentId}",
      {
        params: {
          path: { version: "1", branchId, subDepartmentId }, // Path parameter
          query: {
            "Paging.CurrentPosition": currentPosition,
            "Paging.FetchNext": fetchNext,
            "Paging.FetchAll": fetchAll,
            searchText: searchText,
          },
        },
      }
    ));

    if (!data?.data || !data?.success || error) {
      throw new ApplicationError("Failed to search products");
    }

    const products = this.mapToApplicationProduct(data.data);

    return {
      products,
      loading: false,
      count: data.data?.count || 0,
      currentPosition: data.data?.currentPosition || 0,
    };
  }

  async getProductsBySubDepartments(
    branchId: number,
    subDepartmentId: number,
    currentPosition: number,
    fetchNext: number,
    fetchAll: boolean
  ): Promise<ApplicationProductList> {
    const { data, error } = await this.withRetry(() => client.GET(
      "/v{version}/api/product/branch/{branchId}/subdepartment/{subDepartmentId}",
      {
        params: {
          path: { version: "1", branchId, subDepartmentId },
          query: {
            "Paging.CurrentPosition": currentPosition,
            "Paging.FetchNext": fetchNext,
            "Paging.FetchAll": fetchAll,
          },
        },
      }
    ));

    if (!data?.data || !data?.success || error) {
      throw new ApplicationError(
        "Failed to get products by sub department"
      );
    }
    const products = this.mapToApplicationProduct(data.data);

    return {
      products: products,
      loading: false,
      count: data.data?.count || 0,
      currentPosition: data.data?.currentPosition || 0,
    };
  }

  async getProduct(
    productId: number,
    branchId: number
  ): Promise<ApplicationProductDetail> {
    const { data, error } = await this.withRetry(() => client.GET(
      "/v{version}/api/product/{productId}/branch/{branchId}",
      {
        params: {
          path: { version: "1", productId, branchId },
        },
      }
    ));

    if (!data?.data || !data?.success || error) {
      throw new ApplicationError("Failed to get product");
    }

    return {
      id: data.data?.id ?? 0,
      name: data.data?.name ?? "",
      description: data.data?.description ?? "",
      image: data.data?.image ?? "",
      plu: data.data.plu ?? "",
      active: data.data.active ?? false,
      information: data.data?.productInformation ?? {},
      suggestedQuantity: data.data?.suggestedQuantity ?? 0,
      supplierSizeUnit: data.data?.supplierSizeUnit
        ? mapStringToEnum(ApplicationSizeUnit, data.data.supplierSizeUnit)
        : undefined,
      branches:
        data.data.branches?.map(
          (branch: components["schemas"]["BranchDTO"]) => ({
            id: branch.id ?? 0,
            name: branch.name ?? "",
          })
        ) ?? [],
      vatId: data.data.vatId ?? 0
    };
  }


  async getProductPriceHistory(
    productId: number,
    branchId: number,
  ): Promise<ApplicationProductCostPriceHistory> {
    const { data, error } = await this.withRetry(() =>
      client.GET("/v{version}/api/product/{productId}/branch/{branchId}/history", {
        params: {
          path: { version: "1", productId, branchId },
        },
      })
    );

    if (!data?.data || !data?.success || error) {
      throw new ApplicationError("Failed to get product");
    }

    return {
      id: data.data.id ?? 0,
      name: data.data.name ?? "",
      image: data.data.image ?? "",
      priceHistory:
        data.data.priceHistory?.map((x) => ({
          branch: x.branch!,
          supplierName: x.supplierName!,
          orderDate: new Date(x.orderDate!),
          orderQuantity: x.orderQuantity ?? 0,
          costPrice: x.costPrice ?? 0,
          unitPrice: x.unitPrice ?? 0,
          size: x.size ?? 0,
          sizeUnit: mapStringToEnum(
            ApplicationSizeUnit,
            x.sizeUnit ?? "Unknown"
          ),
        })) ?? [],
    };
  }

  async upsertBranchProduct(
    branchId: number,
    productId: number,
    active: boolean
  ): Promise<void> {
    const { data, error } = await this.withRetry(() => client.POST(
      "/v{version}/api/product/branchproduct",
      {
        params: {
          path: { version: "1" },
        },
        body: {
          branchId,
          productId,
          active,
        },
      }
    ));

    if (!data || error) {
      throw new ApplicationError("Failed to get product");
    }
  }

  async getBestSupplierProduct(
    productId: number,
    branchId: number
  ): Promise<ApplicationBestSupplierProduct> {
    const { data, error } = await this.withRetry(() => client.GET(
      "/v{version}/api/product/branch/{branchId}/product/{productId}/best",
      {
        params: {
          path: { version: "1", branchId, productId },
        },
      }
    ));

    if (!data?.success || error) {
      throw new ApplicationError("Unable to find product for any supplier");
    }

    return {
      supplierId: data.data?.supplier?.id ?? 0,
      supplierName: data.data?.supplier?.name ?? "",
      supplierAddressLine1: data.data?.supplier?.addressLine1 ?? "",
      supplierAddressLine2: data.data?.supplier?.addressLine2 ?? "",
      supplierAddressLine3: data.data?.supplier?.addressLine3 ?? "",
      supplierCity: data.data?.supplier?.city ?? "",
      supplierPostcode: data.data?.supplier?.postcode ?? "",
      deliveryDay: this.mapDeliveryDay(data.data?.deliveryDay ?? null),
      price: data.data?.supplierProduct?.price ?? 0,
      openPricing: data.data?.supplierProduct?.openPricing ?? false,
      size: data.data?.supplierProduct?.size ?? 0,
      origin: data.data?.supplierProduct?.origin ?? "",
      information: data.data?.supplierProduct?.productInformation ?? {},
      sizeUnit: mapStringToEnum(
        ApplicationSizeUnit,
        data.data?.supplierProduct?.sizeUnit ?? "Unknown"
      ),
      minimumOrderQuantity: data.data?.supplierProduct?.minimumOrderQuantity,
      layerSize: data.data?.supplierProduct?.layerSize,
      palletSize: data.data?.supplierProduct?.palletSize,
      fullPalletQuantity: data.data?.supplierProduct?.layerSize && data.data?.supplierProduct?.palletSize
        ? data.data.supplierProduct.layerSize * data.data.supplierProduct.palletSize
        : null,
      requiresOrderApproval: data.data?.supplier?.requiresOrderApproval ?? false,
      productOrderingType: data.data?.supplier?.productOrderingType ?? "Cases",
    };
  }

  private mapDeliveryDay(data: components["schemas"]["DeliveryDayDTO"] | null,): ApplicationDeliveryDay | undefined {
    if (!data) {
      return undefined;
    }

    return {
      date: data?.date ? new Date(data?.date) : new Date(),
      day: mapStringToEnum(ApplicationDay, data?.day ?? "Monday")
    }
  }

  private mapToApplicationProduct(
    data: components["schemas"]["GetProductsResponse"] | undefined
  ): ApplicationProduct[] {

    if (!data) {
      return [];
    }

    return (
      data.products?.map((d) => ({
        id: d.id ?? 0,
        department: d.department == null ? undefined : this.MapToDepartment(d.department),
        subDepartment: d.subDepartment == null ? undefined : this.MapToSubDepartment(d.subDepartment),
        name: d.name ?? "",
        image: d.image ?? "",
        plu: d.plu ?? "",
        order: d.order ?? 0,
        canDelete: d.canDelete ?? false,
        vat: this.mapToVat(d.vat!)
      })) || []
    );
  }


  private MapToDepartment(dept: components["schemas"]["DepartmentDTO"]): ApplicationDepartment {
    return {
      id: dept.id ?? 0,
      name: dept.name ?? "",
      active: dept.active ?? false,
      canDelete: dept.canDelete ?? false,
      orderTemplate: mapStringToEnum(ApplicationOrderTemplate, dept.orderTemplate ?? ""),
      orderingMode: mapStringToEnum(ApplicationOrderingMode, dept.orderingMode ?? "")
    }
  }

  private MapToSubDepartment(dept: components["schemas"]["SubDepartmentDTO"]): ApplicationSubDepartment {
    return {
      id: dept.id ?? 0,
      departmentId: dept.departmentId ?? 0,
      description: dept.description ?? "",
      name: dept.name ?? "",
      active: dept.active ?? false,
      canDelete: dept.canDelete ?? false,
      productSelectionMode: mapStringToEnum(ApplicationProductSelectionMode, dept.productSelectionMode ?? "")
    }
  }

  private mapToVat(vat: components["schemas"]["VatDTO"]): ApplicationVAT {
    return {
      id: vat.id ?? 0,
      description: vat.description ?? "",
      rate: vat.rate ?? 0
    }
  }
}
